import React, { useState, useEffect, useContext } from 'react';
import { NavLink } from 'react-router-dom';
import '../../styles/Sidebar.css';
import { useDispatch, useSelector } from 'react-redux';
import { ProfileContext } from '../../contexts/ProfileContext';

import dashboardIcon from '../../assets/icons/dashboard.svg';
import whiteDashboardIcon from '../../assets/icons/whiteDashboard.svg';
import whiteDevicesIcon from '../../assets/icons/whiteDevices.svg';
import whiteMembersIcon from '../../assets/icons/whiteMembers.svg';
import whiteReportsIcon from '../../assets/icons/whiteReports.svg';
import whiteUsersIcon from '../../assets/icons/whiteUsers.svg';
import devicesIcon from '../../assets/icons/devices.svg';
import usersIcon from '../../assets/icons/users.svg';
import reportsIcon from '../../assets/icons/reports.svg';
import burgerIcon from '../../assets/icons/burger.svg';
import membersIcon from '../../assets/icons/members.svg';
import ebrLogo from '../../assets/icons/ebrSidebar.jpg';

const Sidebar = () => {
  const dispatch = useDispatch();
  const { brandingData, refreshBranding } = useContext(ProfileContext);
  const [logoSrc, setLogoSrc] = useState(ebrLogo);
  const [imageError, setImageError] = useState(false);


  // Update logo source when branding data changes
  useEffect(() => {
    if (brandingData?.Picture && !imageError) {
      setLogoSrc(brandingData.Picture);
    } else {
      setLogoSrc(ebrLogo);
    }
  }, [brandingData, imageError]);

  // Handle image load error (e.g., expired presigned URL)
  const handleImageError = async () => {
    console.log('Logo image failed to load, refreshing branding data...');
    setImageError(true);
    
    try {
      // Try to refresh the branding data to get a new presigned URL
      await refreshBranding();
      setImageError(false);
    } catch (error) {
      console.error('Failed to refresh branding data:', error);
      // Fall back to default logo
      setLogoSrc(ebrLogo);
    }
  };

  const [isSidebarVisible, setSidebarVisible] = useState(false);

  const toggleSidebar = () => {
    if (window.innerWidth <= 480) {
      setSidebarVisible(prev => !prev);
    } else {
      setSidebarVisible(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (window.innerWidth <= 480 &&
        isSidebarVisible &&
        !e.target.closest('.sidebar') &&
        !e.target.closest('.burger-icon')) {
        setSidebarVisible(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isSidebarVisible]);

  useEffect(() => {
    const handleTouchStart = (e) => {
      if (window.innerWidth <= 480 && isSidebarVisible) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    return () => document.removeEventListener('touchstart', handleTouchStart);
  }, [isSidebarVisible]);

  return (
    <>
      <div className="burger-icon" onClick={toggleSidebar}>
        <img src={burgerIcon} alt="Burger Icon" />
      </div>

      <div className={`sidebar ${isSidebarVisible ? 'visible' : ''}`}>
        <div className="sidebar-brand">
          <img 
            src={logoSrc} 
            alt="Eberhard Logo" 
            className="ebr-logo"
            onError={handleImageError}
          />
        </div>
        <nav className="sidebar-nav" onClick={toggleSidebar}>
          <NavLink
            to="/dashboard"
            className={({ isActive }) => isActive ? "active-link" : ""}
          >
            {({ isActive }) => (
              <div className="nav-item">
                <img
                  src={isActive ? whiteDashboardIcon : dashboardIcon}
                  alt="Dashboard Icon"
                  className="nav-icon"
                />
                Dashboard
              </div>
            )}
          </NavLink>
          <NavLink to="/devices" className={({ isActive }) => isActive ? "active-link" : ""}>
            {({ isActive }) => (
              <div className="nav-item">
                <img
                  src={isActive ? whiteDevicesIcon : devicesIcon}
                  alt="Devices Icon"
                  className="nav-icon"
                />
                Devices
              </div>
            )}
          </NavLink>
          <NavLink to="/users" className={({ isActive }) => isActive ? "active-link" : ""}>
            {({ isActive }) => (
              <div className="nav-item">
                <img
                  src={isActive ? whiteUsersIcon : usersIcon}
                  alt="Users Icon"
                  className="nav-icon"
                />
                Users
              </div>
            )}
          </NavLink>
          <NavLink to="/reports" className={({ isActive }) => isActive ? "active-link" : ""}>
            {({ isActive }) => (
              <div className="nav-item">
                <img
                  src={isActive ? whiteReportsIcon : reportsIcon}
                  alt="Reports Icon"
                  className="nav-icon"
                />
                Reports
              </div>
            )}
          </NavLink>
          <NavLink to="/members" className={({ isActive }) => isActive ? "active-link" : ""}>
            {({ isActive }) => (
              <div className="nav-item">
                <img
                  src={isActive ? whiteMembersIcon : membersIcon}
                  alt="Members Icon"
                  className="nav-icon"
                />
                Internal Users
              </div>
            )}
          </NavLink>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;